import { Box, Button, Divider, Flex, Image, Text, Spacer, HStack, Stack } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NftResource } from "../../../components/NftResource";
import { colors } from "../../../core/constants/colors";
import { INft, INftMetadata } from "../../../core/models/nft";
import { formatNumber, toPlainString } from "../../../core/utils/number";
import { useAppSelector } from "../../../store/hook";
import { IoIosHeart, IoIosHeartEmpty } from "react-icons/io";
import { userService } from "../../../services/user.service";
import { nftService } from "../../../services/nft.service";
import Web3 from "web3";
import { generateNonce } from "../../../core/utils/securityUtils";
import { useTranslation } from "react-i18next";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import BigNumber from "bignumber.js";
import { uriToHttps } from "../../../core/utils/ipfs";
import axios from "axios";

interface Props {
  nft: INft;
  account?: string | null | undefined;
}

export const NftItem = React.memo(({ nft, account }: Props) => {
  const { t } = useTranslation([I18N_NAMESPACE.MARKETPLACE]);
  const navigate = useNavigate();
  const supportedTokens = useAppSelector((state) => state.token.supportedTokens);
  const token = useMemo(
    () =>
      supportedTokens.allIds.length > 0 && nft.asset_type?.token_address
        ? supportedTokens.byId[nft.asset_type?.token_address]
        : null,
    [supportedTokens, nft.asset_type?.token_address]
  );

  const [isHoverFavorite, setIsHoverFavorite] = useState<boolean>(false);
  const [favorites, setFavorites] = useState<number>(nft.favorites ?? 0);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);

  const [metadata, setMetata] = useState<INftMetadata>({
    image: nft.image,
    name: nft.name,
  });

  useEffect(() => {
    async function getMetadata() {
      if (nft.token_uri) {
        const uri = uriToHttps(nft.token_uri);
        if (!uri) return;

        const result = await axios.get(uri);
        setMetata(result.data);
      }
    }

    getMetadata();
  }, [nft.token_uri]);

  const getNumberFavorite = async () => {
    const result = await nftService.getNumberFavorite(nft.token_address, nft.token_id);
    if (result?.data !== null && result?.data !== undefined) {
      setFavorites(result.data);
    }
  };

  const onPressItem = () => {
    navigate(`/assets/${nft.token_address}/${nft.token_id}`);
  };

  const addFavorite = async () => {
    if (account) {
      const response = await userService.getProfileById(account);

      if (response.data) {
        const result = await nftService.addFavorite(nft.token_address, nft.token_id, account);
        if (result.data) {
          setIsFavorite(true);
          nft.is_favorite = false;
          getNumberFavorite();
        }
      } else {
        try {
          const web3 = new Web3((window as any).web3.currentProvider);
          await web3.eth.sign(web3.utils.fromUtf8(`${generateNonce(16, "hex")}`), account);
          const result = await nftService.addFavorite(nft.token_address, nft.token_id, account);
          if (result.data) {
            setIsFavorite(true);
            nft.is_favorite = false;
            getNumberFavorite();
          }
        } catch {}
      }
    }
  };

  const removeFavorite = async () => {
    if (account) {
      const response = await userService.getProfileById(account);

      if (response.data) {
        const result = await nftService.removeFavorite(nft.token_address, nft.token_id, account);
        if (result.data) {
          setIsFavorite(false);
          nft.is_favorite = false;
          getNumberFavorite();
        }
      } else {
        try {
          const web3 = new Web3((window as any).web3.currentProvider);
          await web3.eth.sign(web3.utils.fromUtf8(`${generateNonce(16, "hex")}`), account);
          const result = await nftService.removeFavorite(nft.token_address, nft.token_id, account);
          if (result.data) {
            setIsFavorite(false);
            nft.is_favorite = false;
            getNumberFavorite();
          }
        } catch {}
      }
    }
  };

  const FavoriteDetail = () => {
    return (
      <>
        {isFavorite || nft.is_favorite ? (
          <HStack spacing={1}>
            <IoIosHeart
              onClick={(e) => {
                e.stopPropagation();
                removeFavorite();
              }}
              cursor={"pointer"}
            />
            <Text fontSize={"sm"} fontWeight={500}>
              {favorites}
            </Text>
          </HStack>
        ) : (
          <HStack
            spacing={1}
            onMouseEnter={() => setIsHoverFavorite(true)}
            onMouseLeave={() => setIsHoverFavorite(false)}
          >
            {isHoverFavorite ? (
              <IoIosHeart
                onClick={(e) => {
                  e.stopPropagation();
                  addFavorite();
                }}
                cursor={"pointer"}
              />
            ) : (
              <IoIosHeartEmpty cursor={"pointer"} />
            )}
            <Text fontSize={"sm"} fontWeight={500}>
              {favorites}
            </Text>
          </HStack>
        )}
      </>
    );
  };

  return (
    <Flex
      borderWidth="1px"
      borderRadius="lg"
      flexDirection="column"
      overflow="hidden"
      onClick={onPressItem}
      _hover={{ boxShadow: "lg", transition: "all 0.1s ease 0s", transform: "translateY(-4px)" }}
      cursor={"pointer"}
    >
      <NftResource image={metadata.image} animationUrl={metadata.animation_url} muted />

      <Box padding={3}>
        <Flex flex={1} flexDirection="column">
          <Flex justifyContent="space-between" flex={1} gridGap={"20px"}>
            <Box flex={1} width={"100%"}>
              <Box color="gray.500" fontWeight="semibold" letterSpacing="wide" fontSize="xs" textTransform="uppercase">
                {nft?.token_name || ""}
              </Box>
              <Box mt="1" fontWeight="semibold" as="h4" lineHeight="tight" isTruncated>
                {metadata.name || ""}
              </Box>
            </Box>
          </Flex>

          <Box marginTop={2}>
            <Flex alignItems="center">
              <Image src={token?.logoUrl} height="16px" width="16px" marginRight="4px" />
              <Text fontSize="sm" color={colors.primary1} fontWeight="700">
                {nft.price
                  ? formatNumber(new BigNumber(nft.price).div(Math.pow(10, token?.decimals || 0)).toNumber())
                  : ""}
              </Text>
            </Flex>
          </Box>
        </Flex>

        <Box m="4px 0px">
          <Divider />
        </Box>

        <Button colorScheme={"blue"} margin={"8px 0px"} width={"full"}>
          {t("Buy")}
        </Button>

        <Box>
          <Box m="4px 0px">
            <Divider />
          </Box>
          {token?.underlying !== "eth" ? (
            <Box padding={"6px 0px"}>
              <Text fontSize={"14px"} fontWeight={500}>
                {t("Pay today at")}
              </Text>
              <Flex alignItems="center" justifyContent="space-between" w={"100%"} marginTop={1}>
                <Flex alignItems={"center"}>
                  <Image src={token?.logoUrl} height="16px" width="16px" margin="0px 4px" />
                  <Text fontSize="sm" color={colors.primary1} fontWeight="700">
                    {nft.price
                      ? formatNumber(
                          new BigNumber(nft.price)
                            .div(4)
                            .div(Math.pow(10, token?.decimals || 0))
                            .toNumber()
                        )
                      : ""}
                  </Text>
                </Flex>
                <FavoriteDetail />
              </Flex>
            </Box>
          ) : (
            <>
              <Flex alignItems="end" justifyContent="end" fontSize={"14px"} fontWeight={500} padding={"4px"}>
                <FavoriteDetail />
              </Flex>
            </>
          )}
        </Box>
      </Box>
    </Flex>
  );
});
